/*
 *
 * Dashboard saga
 *
 */

import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  defaultAction,
  defaultActionSuccess,
  defaultActionError,
  fetchInformation,
  fetchInformationSuccess,
  fetchInformationError,
  getTreeTokenSuccess,
  getTreeTokenError,
  getTreeToken,
  getWalletSummary,
  getWalletSummarySuccess,
  getWalletSummaryError,
} from './actions';
import apiClient from '../../services/apiClient';
import { informationListResponseSchema } from '../../apischema/information/informationResponse';
import config from '../../config';
import { appSelectors } from '../App/selectors';
import { getAuthToken } from '../../utils/helper';
import { API_ENDPOINTS, APIHeadersWithAuth, getCountryApiEndpoints } from '../../globalConstants';
import { tokenResponseSchema } from '../../apischema/sss/sssTokenResponse';
import { walletSummaryResponseSchema } from '../../apischema/savingwallet';
import { selectCountryCode } from '../../store/countrySlice';


const DEMO_API = 'https://jsonplaceholder.typicode.com/posts/1';

function* defaultActionSaga(): Generator<any, void, any> {
  try {
    const response = yield call(apiClient.request, DEMO_API, { method: 'GET' });
    // Uncomment and use your schema for validation
    // const parsed = defaultApiResponseSchema.safeParse(response);
    // if (!parsed.success) {
    //   throw new Error('Invalid API response: ' + JSON.stringify(parsed.error.issues));
    // }
    // yield put(defaultActionSuccess(parsed.data));
    yield put(defaultActionSuccess(response));
  } catch (error) {
    yield put(
      defaultActionError(
        error instanceof Error ? error.message : 'An error occurred',
      ),
    );
  }
}

function* fetchInformationSaga(
  action: ReturnType<typeof fetchInformation>,
): Generator<any, void, any> {
  try {
    const { params } = action.payload;
    const countryCode: string = yield select(selectCountryCode);
    const endpoints = getCountryApiEndpoints(countryCode, 'dashboardPage');
    const url = endpoints.information.GET_ENABLED(params.page || 1, params.pageSize || 10);
    let token: string | null = yield select(appSelectors.token);

    if (!token) {
      token = yield call(getAuthToken);
    }

    if (!token) {
      throw new Error('Authentication token not available');
    }



    const headers = APIHeadersWithAuth(token);



    const response = yield call(apiClient.request, url, {
      method: 'GET',
      headers: { ...headers },
    });

    // Validate the response using zod schema
    const parsed = informationListResponseSchema.safeParse(response);
    if (!parsed.success) {

      throw new Error('Invalid API response format');
    }

    yield put(fetchInformationSuccess(parsed.data.data));
  } catch (error) {

    yield put(
      fetchInformationError(
        error instanceof Error ? error.message : 'Failed to fetch information',
      ),
    );
  }
}

function* getTreeTokenSaga(): Generator<any, void, any> {
  try {
    const countryCode: string = yield select(selectCountryCode);
    const endpoints = getCountryApiEndpoints(countryCode, 'dashboardPage');
    const url = endpoints.sss.GET_TREE_TOKEN;
    let token: string | null = yield select(appSelectors.token);

    if (!token) {
      token = yield call(getAuthToken);
    }

    if (!token) {
      throw new Error('Authentication token not available');
    }

    const response = yield call(apiClient.request, url, {
      method: 'GET',
      headers: APIHeadersWithAuth(token),
    });

    // Validate the response using zod schema
    const parsed = tokenResponseSchema.safeParse(response);
    if (!parsed.success) {

      throw new Error('Invalid API response format');
    }

    yield put(getTreeTokenSuccess(parsed.data.data));
  } catch (error) {

    yield put(
      getTreeTokenError(
        error instanceof Error ? error.message : 'Failed to fetch information',
      ),
    );
  }
}

function* getWalletSummarySaga(): Generator<any, void, any> {
  try {
    const countryCode: string = yield select(selectCountryCode);
    const endpoints = getCountryApiEndpoints(countryCode, 'dashboardPage');
    const url = endpoints.savingWallet.GET_SUMMARY;
    let token: string | null = yield select(appSelectors.token);

    if (!token) {
      token = yield call(getAuthToken);
    }

    if (!token) {
      throw new Error('Authentication token not available');
    }

    const response = yield call(apiClient.request, url, {
      method: 'GET',
      headers: APIHeadersWithAuth(token),
    });

    // Validate the response using zod schema
    const parsed = walletSummaryResponseSchema.safeParse(response);
    if (!parsed.success) {

      throw new Error('Invalid API response format');
    }

    yield put(getWalletSummarySuccess(parsed.data));
  } catch (error) {

    yield put(
      getWalletSummaryError(
        error instanceof Error ? error.message : 'Failed to fetch wallet summary',
      ),
    );
  }
}

export default function* dashboardSaga() {
  yield takeLatest(defaultAction.type, defaultActionSaga);
  yield takeLatest(fetchInformation.type, fetchInformationSaga);
  yield takeLatest(getTreeToken.type, getTreeTokenSaga);
  yield takeLatest(getWalletSummary.type, getWalletSummarySaga);
}
